import { ReactNode, useState } from 'react';
import clsx from 'clsx';
import AskAicaV2 from '../../modules/Dashboard/DashboardModules/Overview/components/AskAicaV2/AskAicaV2';
import { ASK_AICA_SURFACE } from '../../modules/Dashboard/DashboardModules/Overview/components/AskAicaV2/context/AskAicaSurfaceContext';
import styles from './PortfolioAskAicaEntry.module.scss';

interface PortfolioAskAicaEntryOptions {
    /** Visibility gate — when false both the button and the chat are null. */
    enabled: boolean;
    /** What the button shows (the AskAicaOrb pill or the chatbot icon). */
    content: ReactNode;
    testId: string;
    /** Deals / ManageUser pin the button to the bottom-right corner on desktop;
     * Portfolio places it inside the table card instead. */
    floating?: boolean;
}

/**
 * The org-less Ask AICA launcher shared by Portfolio, Deals and ManageUser —
 * the entry button plus the portfolio-surface chat, and the one open/close
 * timing contract between them. The button stays mounted (faded + disabled)
 * while the chat is open, so MobileBottomTabs' row width never jumps, and
 * fades back in the moment the chat starts closing.
 */
export function usePortfolioAskAicaEntry({ enabled, content, testId, floating = false }: PortfolioAskAicaEntryOptions) {
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const entryButton = enabled ? (
        <button
            type="button"
            className={clsx(styles.AskAicaEntryButton, {
                [styles.Floating]: floating,
                [styles.AskAicaEntryButtonHidden]: isOpen && !isClosing,
            })}
            onClick={() => setIsOpen(true)}
            disabled={isOpen}
            aria-hidden={isOpen}
            aria-label="Open Ask AICA"
            data-testid={testId}
        >
            {content}
        </button>
    ) : null;

    const chat =
        enabled && isOpen ? (
            <AskAicaV2
                surface={ASK_AICA_SURFACE.PORTFOLIO}
                onCloseStart={() => setIsClosing(true)}
                onClose={() => {
                    setIsOpen(false);
                    setIsClosing(false);
                }}
            />
        ) : null;

    return { entryButton, chat, isOpen };
}

export const portfolioAskAicaEntryIconClass = styles.AskAicaEntryIcon;
